/**
 * "Accept and commit" confirmation for step 4 (f10.7, design D10): keys, rate
 * rows written and superseded, mappings to be created, excluded keys and the
 * step's progress after commit. Every number is the server's.
 */

import { Check, Info } from 'lucide-react';

import type { SalesRateReview } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { refusalMessage } from './salesRatesView';

export function SalesRatesCommitDialog({
  review,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  review: SalesRateReview;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const plan = review.commit_plan;
  const completes = plan.committed_scopes_after_commit === plan.expected_scopes;
  const stale = plan.later_steps_becoming_stale;
  return (
    <Modal
      title="Accept and commit?"
      subtitle={`${review.batch.scope?.name ?? 'COSMA sales rates'} · ${
        review.batch.original_filename ?? `batch #${review.batch.batch_number}`
      }`}
      onClose={onClose}
      onSubmit={onConfirm}
      busy={busy}
      submitLabel="Accept and commit"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={refusalMessage(error)}
    >
      <dl className="dl cols-2 mb-4">
        <div>
          <dt>Rate keys committed</dt>
          <dd>
            {plan.keys_committed.toLocaleString()} &middot; {plan.keys_excluded} excluded
          </dd>
        </div>
        <div>
          <dt>Rate rows</dt>
          <dd>
            {plan.rate_rows_to_write.toLocaleString()} written &middot; {plan.rate_rows_superseded.toLocaleString()}{' '}
            superseded
          </dd>
        </div>
        <div>
          <dt>Resource BU mappings created</dt>
          <dd>
            {plan.mappings_to_add.length
              ? plan.mappings_to_add
                  .map((m) => `${m.raw_value} → ${m.canonical_psu}${m.country_code ? ` / ${m.country_code}` : ''}`)
                  .join(', ')
              : 'None'}
          </dd>
        </div>
        <div>
          <dt>Step after commit</dt>
          <dd>
            {plan.committed_scopes_after_commit} of {plan.expected_scopes}
            {completes ? ' — complete' : ''}
          </dd>
        </div>
        <div>
          <dt>Later completed steps become stale</dt>
          <dd>{completes && stale.length ? stale.join(', ') : 'None'}</dd>
        </div>
      </dl>
      <div className="alert alert-info">
        <Info aria-hidden="true" />
        <div className="a-body">
          Each key gets twelve monthly rates and one annual rate, in {review.batch.scope?.currency_code ?? 'the scope currency'}.
          The job writes everything in one transaction &mdash; all of it, or nothing. Earlier rates of this scope are
          superseded, never deleted, and no forecast line is changed.
        </div>
      </div>
    </Modal>
  );
}
